import { useState, useCallback } from 'react';
import { ApiError, LoadingState } from '../types';

interface ApiHookResult<T> extends LoadingState {
  data: T | null;
  execute: (...args: any[]) => Promise<T | null>;
  reset: () => void;
}

export function useApi<T = any>(
  apiFunction: (...args: any[]) => Promise<any>
): ApiHookResult<T> {
  const [state, setState] = useState<LoadingState & { data: T | null }>({
    data: null,
    isLoading: false,
    error: null,
  });

  const execute = useCallback(
    async (...args: any[]): Promise<T | null> => {
      setState(prev => ({ ...prev, isLoading: true, error: null }));

      try {
        const response = await apiFunction(...args);
        const data = response.data;
        setState({ data, isLoading: false, error: null });
        return data;
      } catch (error: any) {
        const errorMessage = extractErrorMessage(error);
        setState(prev => ({ 
          ...prev, 
          isLoading: false, 
          error: errorMessage 
        }));
        return null;
      }
    },
    [apiFunction]
  );

  const reset = useCallback(() => {
    setState({ data: null, isLoading: false, error: null });
  }, []);

  return {
    data: state.data,
    isLoading: state.isLoading,
    error: state.error,
    execute,
    reset,
  };
}

function extractErrorMessage(error: any): string {
  if (error.response?.data) {
    const apiError = error.response.data as ApiError;
    
    // If there are field errors, show the first one
    if (apiError.fieldErrors && Object.keys(apiError.fieldErrors).length > 0) {
      const firstError = Object.values(apiError.fieldErrors)[0];
      return firstError;
    }
    
    return apiError.message || 'An error occurred';
  }
  
  if (error.message) {
    return error.message;
  }
  
  return 'An unexpected error occurred';
}